import { normalizeExerciseName } from '../utils/exerciseIdentity';

const guide = (summary, setup, execution, cues) => ({
  summary,
  setup,
  execution,
  cues,
  curated: true,
});

const GUIDES = Object.freeze({
  'step out jack': guide(
    'Step one foot out at a time instead of jumping, keeping the torso tall and the movement smooth.',
    [
      'Stand tall with feet together or hip-width and arms relaxed.',
      'Keep the knees soft and brace lightly through the trunk.',
    ],
    [
      'Step one foot out while sweeping both arms overhead.',
      'Return that foot, then repeat to the other side at a steady rhythm.',
    ],
    [
      'Keep it low impact—do not hop between sides.',
      'Keep the knees tracking with the toes.',
    ],
  ),
  'power jack': guide(
    'Use a stronger jump than a standard jack while keeping every landing quiet and controlled.',
    [
      'Stand tall with feet together and brace the trunk.',
      'Use only a speed and range you can land without the knees collapsing inward.',
    ],
    [
      'Jump the feet wide as the arms move overhead, then spring back to the start.',
      'Absorb each landing through the ankles, knees, and hips before the next rep.',
    ],
    [
      'Power comes from a crisp jump, not a hard landing.',
      'Stop the set when landing control deteriorates.',
    ],
  ),
  'incline push up': guide(
    'Keep a straight line from head to heels while pressing from a stable elevated surface.',
    [
      'Place hands on a sturdy bench, counter, or rail slightly wider than shoulder width.',
      'Walk the feet back until the body forms one straight line from head to heels.',
    ],
    [
      'Lower the chest toward the surface with elbows roughly 30–45° from the torso.',
      'Press the surface away until the elbows are straight without letting the hips sag.',
    ],
    [
      'Use a higher surface to make the movement easier.',
      'Keep the trunk rigid and the neck neutral.',
    ],
  ),
  'decline push up': guide(
    'Elevate the feet while keeping the same rigid push-up body line and controlled elbow path.',
    [
      'Place the feet on a stable low bench or step and the hands slightly wider than shoulder width.',
      'Brace the abs and glutes so the hips stay aligned with the shoulders and heels.',
    ],
    [
      'Lower the chest toward the floor under control with elbows roughly 30–45° from the torso.',
      'Press back to full arm extension without losing the rigid body position.',
    ],
    [
      'Use only enough foot elevation to keep clean reps.',
      'Do not let the lower back sag as fatigue builds.',
    ],
  ),
  'pike push up': guide(
    'Keep the hips high in an inverted-V position and lower the head toward the floor using the shoulders and triceps.',
    [
      'Place the hands around shoulder width with fingers spread.',
      'Walk the feet toward the hands until the hips are high and the torso angles toward the floor.',
      'Keep the abdomen braced and the neck neutral while looking toward the floor.',
    ],
    [
      'Bend the elbows and lower the head toward the floor slightly forward of the hands.',
      'Keep the hips high instead of flattening into a standard push-up position.',
      'Press the floor away until the elbows straighten and return to the inverted-V position.',
    ],
    [
      'Think hips high and head down.',
      'Do not turn the movement into a standard horizontal push-up.',
      'Reduce the range or elevate the hands if shoulder control is limited.',
    ],
  ),
  'diamond push up': guide(
    'Keep the hands close beneath the chest and the body rigid while the elbows bend back rather than flaring wide.',
    [
      'Start in a strong plank with the hands close together beneath the chest.',
      'Keep the body in one straight line from head through heels.',
      'Brace the abdomen and glutes before lowering.',
    ],
    [
      'Lower the chest toward the hands while keeping the elbows relatively close to the torso.',
      'Stop at the deepest position you can control without the shoulders rolling forward.',
      'Press through the hands until the elbows straighten.',
    ],
    [
      'Do not force the hands into an uncomfortable diamond shape.',
      'Keep the hips from sagging.',
      'Use a wider close-grip position if the wrists are uncomfortable.',
    ],
  ),
  'close grip push up': guide(
    'Use a narrower hand position than a standard push-up and keep the elbows tracking close to the torso.',
    [
      'Set the hands around shoulder width or slightly narrower.',
      'Create one straight line from head through hips to heels.',
      'Brace the trunk before beginning the repetition.',
    ],
    [
      'Lower the chest under control while the elbows travel mostly backward.',
      'Maintain the rigid body line throughout the descent.',
      'Press the floor away until the elbows straighten.',
    ],
    [
      'Do not let the elbows flare sharply outward.',
      'Keep the hips aligned with the shoulders and heels.',
      'Widen the hands slightly if the wrists or elbows are uncomfortable.',
    ],
  ),
  'wide push up': guide(
    'Use a hand position wider than shoulder width while keeping the trunk rigid and the shoulder position controlled.',
    [
      'Place the hands wider than a standard push-up but within a comfortable shoulder range.',
      'Set the body in one straight line from head through heels.',
      'Brace the abs and glutes before lowering.',
    ],
    [
      'Lower the chest toward the floor under control.',
      'Let the elbows move outward naturally without forcing them to 90 degrees.',
      'Press evenly through both hands to return to the top.',
    ],
    [
      'Do not use an excessively wide hand position.',
      'Keep the hips from sagging.',
      'Stop the descent before the shoulders lose control.',
    ],
  ),
  'machine chest press': guide(
    'Keep the back supported and press the machine handles forward from chest level without letting the shoulders roll forward.',
    [
      'Adjust the seat so the handles begin around mid-chest height.',
      'Sit with the back and head supported and the feet planted.',
      'Set the shoulder blades gently back and down before pressing.',
    ],
    [
      'Press the handles forward until the elbows are nearly straight.',
      'Keep the wrists stacked with the forearms throughout the press.',
      'Return under control until you reach a comfortable chest stretch.',
    ],
    [
      'Do not let the shoulders roll forward at the end of the press.',
      'Avoid bouncing the weight stack.',
      'Adjust the seat or range if the handles force an uncomfortable shoulder position.',
    ],
  ),
  'resistance band chest press': guide(
    'Press the bands forward from chest level while resisting the backward pull of the anchor.',
    [
      'Secure the band behind you at approximately chest height.',
      'Take a stable split or staggered stance with the handles near the chest.',
      'Brace the trunk so the band tension does not pull the torso backward.',
    ],
    [
      'Press the hands forward until the arms are nearly straight.',
      'Keep the shoulders down and the wrists aligned with the forearms.',
      'Return slowly until the hands come back beside the chest.',
    ],
    [
      'Confirm the anchor is secure before every set.',
      'Do not let the bands snap the hands backward.',
      'Keep the torso stable instead of leaning forward to finish the rep.',
    ],
  ),
  'split squat': guide(
    'Use a stable staggered stance and lower mostly straight down while the front knee tracks with the toes.',
    [
      'Take a long staggered stance with both feet stable.',
      'Keep most of the working pressure through the front foot.',
      'Stay tall through the torso and brace lightly.',
    ],
    [
      'Lower the back knee toward the floor while the front knee bends in line with the toes.',
      'Descend only as far as balance and control allow.',
      'Drive through the front foot to return to the top.',
    ],
    [
      'Do not walk the feet on a tightrope line.',
      'Keep the front heel planted.',
      'Shorten the range if balance or knee position deteriorates.',
    ],
  ),
  'bulgarian split squat': guide(
    'Keep the front foot planted while the rear foot is elevated and lower under control through the front leg.',
    [
      'Place the rear foot on a stable bench or support and step the front foot far enough forward for balance.',
      'Keep the front foot flat and the hips facing forward.',
      'Brace the trunk before descending.',
    ],
    [
      'Lower the rear knee toward the floor while the front knee tracks with the toes.',
      'Maintain pressure through the whole front foot.',
      'Drive through the front leg to return to the top.',
    ],
    [
      'Do not let the front knee collapse inward.',
      'Use a lower rear-foot support if balance is poor.',
      'Keep enough stance width to remain stable.',
    ],
  ),
  'single leg deadlift': guide(
    'Balance on one leg and hinge at the hip while the free leg travels backward and the hips remain square.',
    [
      'Stand on the working leg with a soft knee bend.',
      'Brace the trunk and keep the hips square to the floor.',
      'Hold the resistance close to the working leg if weight is used.',
    ],
    [
      'Push the working hip backward as the torso tips forward and the free leg reaches behind you.',
      'Lower only as far as you can maintain balance and a neutral spine.',
      'Drive the working hip forward to return to standing.',
    ],
    [
      'Do not rotate the pelvis open as the free leg rises.',
      'Keep the working foot firmly planted.',
      'Use support for balance if needed while learning the movement.',
    ],
  ),
  'seated calf raise': guide(
    'Move through the ankle while seated: lower the heels under control, rise onto the balls of the feet, and pause at the top.',
    [
      'Sit securely with the balls of the feet supported and the heels free to move.',
      'Position the resistance across the thighs or machine pads as designed.',
      'Keep the knees stable throughout the set.',
    ],
    [
      'Lower the heels into a comfortable calf stretch.',
      'Press through the balls of the feet to raise the heels as high as you can control.',
      'Pause briefly, then lower slowly into the next repetition.',
    ],
    [
      'Do not bounce out of the bottom position.',
      'Keep pressure across the whole forefoot.',
      'Use a controlled range rather than chasing speed.',
    ],
  ),
  'calf press on leg press': guide(
    'Keep the legs nearly straight but unlocked and move the sled only through controlled ankle motion.',
    [
      'Set up securely in the leg press with the hips and back fully supported.',
      'Place the balls of the feet on the lower edge of the platform with the heels free to move.',
      'Keep a slight bend in the knees and secure the sled before beginning.',
    ],
    [
      'Lower the heels through a comfortable ankle range while keeping the knees nearly fixed.',
      'Press through the balls of the feet to point the ankles and move the sled upward.',
      'Pause briefly at the top, then lower under control.',
    ],
    [
      'Do not lock the knees aggressively.',
      'Do not let the feet slide off the platform.',
      'Re-engage the machine safeties before changing position.',
    ],
  ),

  "arm circles": guide(
    "Warm the shoulders with smooth controlled circles while the trunk stays tall and relaxed.",
    ["Stand tall with the feet stable and the arms extended comfortably out to the sides.", "Keep the ribs stacked over the pelvis and relax the shoulders away from the ears."],
    ["Circle the arms smoothly through a comfortable range without jerking.", "Begin with smaller circles and gradually increase the size while maintaining control.", "Reverse direction when programmed and keep the movement even on both sides."],
    ["Move from the shoulders rather than twisting the torso.", "Keep the neck relaxed.", "Use a pain-free range instead of forcing larger circles."],
  ),
  "bear crawl": guide(
    "Keep the knees hovering close to the floor and move opposite hand and foot together while the trunk stays quiet.",
    ["Start on hands and knees with hands under shoulders and knees under hips.", "Brace the abdomen and lift the knees just off the floor.", "Keep the back flat and the hips low."],
    ["Move one hand and the opposite foot forward a short distance at the same time.", "Alternate sides using small controlled steps.", "Maintain the same torso height as you travel."],
    ["Do not let the hips sway side to side.", "Keep the knees close to the floor.", "Use short steps rather than reaching too far."],
  ),
  "bird dog": guide(
    "Extend the opposite arm and leg without letting the pelvis rotate or the lower back arch.",
    ["Begin on all fours with hands under shoulders and knees under hips.", "Brace the abdomen and keep the spine neutral."],
    ["Reach one arm forward as the opposite leg extends behind you.", "Pause when the arm, torso, and leg form a controlled line.", "Return to the start and repeat on the opposite side."],
    ["Keep both hip bones facing the floor.", "Reach long rather than lifting the leg excessively high.", "Move slowly enough that the torso stays still."],
  ),
  "cable chest fly": guide(
    "Bring the cable handles together in a wide hugging arc while keeping a soft, nearly fixed bend in the elbows.",
    ["Set both pulleys at the programmed height and take a stable staggered stance between them.", "Step forward enough to create cable tension with the hands open to the sides.", "Set the shoulders gently back and down and keep a soft bend in the elbows."],
    ["Sweep the arms forward in a controlled arc until the hands meet or nearly meet in front of the chest.", "Pause briefly while keeping the shoulders from rolling forward.", "Return through the same arc until you reach a comfortable chest stretch."],
    ["Keep the elbow angle mostly constant.", "Do not turn the movement into a press.", "Keep the torso stable instead of leaning into the cables."],
  ),
  "cable wood chop": guide(
    "Move the cable diagonally across the body with controlled trunk and hip rotation rather than pulling only with the arms.",
    ["Stand side-on to the cable in a stable stance with both hands on the handle.", "Step far enough from the stack to maintain tension through the programmed path.", "Brace the trunk before beginning the rotation."],
    ["Rotate the ribcage and hips together as the hands travel diagonally across the body.", "Allow the feet to pivot naturally if the programmed range requires it.", "Return slowly against the cable tension."],
    ["Do not wrench the lower back while the hips stay fixed.", "Keep the arms connected to the torso rather than making it an arm swing.", "Control the return as much as the pull."],
  ),
  "chest supported row": guide(
    "Keep the chest supported on the bench and row the elbows toward the ribs without lifting the torso.",
    ["Set an incline bench to a comfortable angle and lie chest-down with the feet secure.", "Let the dumbbells hang beneath the shoulders with the neck neutral.", "Set the shoulders away from the ears before pulling."],
    ["Drive the elbows back toward the ribs while the chest stays on the pad.", "Pause briefly as the shoulder blades draw together.", "Lower the dumbbells until the arms are long again without losing the supported position."],
    ["Keep the chest on the bench.", "Do not shrug the shoulders upward.", "Lead with the elbows instead of curling the weights."],
  ),
  "cossack squat": guide(
    "Shift deeply into one hip while the opposite leg stays long, keeping the working foot planted and the knee aligned.",
    ["Take a wide stance with the feet turned out only as much as needed for comfortable hip motion.", "Brace the trunk and keep the chest tall."],
    ["Shift the hips toward one side and bend that knee while the opposite leg straightens.", "Keep the working heel planted as you descend through a controlled range.", "Drive through the working foot to return to the wide starting stance."],
    ["Track the bent knee in the same direction as the toes.", "Do not force depth beyond your hip or ankle mobility.", "Keep the working foot fully supported."],
  ),
  "dead bug": guide(
    "Keep the trunk braced and the lower back controlled as opposite arm and leg extend away from the body.",
    ["Lie on your back with hips and knees bent around 90 degrees and arms pointed upward.", "Gently brace the abdomen so the ribcage and pelvis remain controlled."],
    ["Slowly extend one leg and the opposite arm toward the floor.", "Stop before the lower back arches or the ribs flare upward.", "Return to the start and alternate sides."],
    ["Move only as far as you can keep the trunk position.", "Exhale as the limbs extend if that helps maintain the brace.", "Keep the movement slow and symmetrical."],
  ),
  "hollow body hold": guide(
    "Hold the ribs and pelvis together so the lower back stays supported while the arms and legs lengthen away.",
    ["Lie on your back and gently flatten the lower-back area toward the floor.", "Lift the shoulders slightly and choose an arm and leg position you can control."],
    ["Extend the legs and arms farther from the body only while the trunk stays braced.", "Hold the position for the programmed time without losing the lower-back position.", "Bring the knees or arms closer to the body to reduce difficulty."],
    ["Do not let the lower back arch away from the floor.", "Keep the neck long instead of pulling the chin hard to the chest.", "Choose the easiest variation that allows a solid hold."],
  ),
  "jump squat": guide(
    "Use a controlled squat to load the legs, jump vertically, and absorb the landing softly before the next repetition.",
    ["Stand with the feet in a comfortable squat stance.", "Brace the trunk and prepare to land through the whole foot."],
    ["Descend into a controlled squat and drive through the floor to jump upward.", "Land softly with the hips and knees bending together.", "Regain balance and alignment before beginning the next repetition."],
    ["Land quietly.", "Keep the knees tracking with the toes.", "Stop the set when landing control deteriorates."],
  ),
  "landmine press": guide(
    "Press the anchored bar upward and forward from the shoulder while keeping the ribs controlled and the torso stable.",
    ["Secure one end of the bar in a landmine attachment and hold the free end near the working shoulder.", "Use a stable standing or half-kneeling position appropriate to the workout.", "Brace the trunk before pressing."],
    ["Press the bar upward and forward along its natural arc.", "Reach a strong finish without leaning the torso backward.", "Lower the bar under control to the shoulder."],
    ["Follow the bar's angled path instead of forcing it straight overhead.", "Keep the ribs from flaring.", "Do not use the legs to drive the bar unless a push-press variation is specifically programmed."],
  ),
  "landmine rotation": guide(
    "Guide the anchored bar in a controlled arc from side to side while the hips and ribcage rotate together.",
    ["Stand facing the landmine anchor with both hands supporting the free end of the bar.", "Use a stable stance with soft knees and the bar held in front of the chest or shoulder line."],
    ["Move the bar in an arc toward one side while rotating through the hips and torso.", "Reverse the motion smoothly through center to the opposite side.", "Allow the feet to pivot so the knees and hips can follow the rotation."],
    ["Do not twist aggressively through a fixed lower back.", "Keep control of the bar through the center.", "Let the hips participate in the rotation."],
  ),
  "lateral bounds": guide(
    "Push explosively sideways from one leg and absorb each landing on the opposite leg before rebounding.",
    ["Stand in an athletic stance with knees and hips softly bent.", "Create enough clear space to jump safely side to side."],
    ["Drive off one leg and jump laterally toward the opposite side.", "Land on the opposite foot with the hip, knee, and ankle flexed to absorb force.", "Stabilize briefly before bounding back unless continuous repetitions are programmed."],
    ["Keep the landing knee aligned with the foot.", "Land softly instead of stiff-legged.", "Reduce jump distance if balance deteriorates."],
  ),
  "lateral lunge": guide(
    "Step wide to one side and sit the hips back over the working leg while the opposite leg remains long.",
    ["Stand tall with enough space to step laterally.", "Brace the trunk and keep both feet pointing generally forward."],
    ["Step to one side and bend that hip and knee as the hips move backward.", "Keep the opposite leg straight or softly bent while the working foot stays planted.", "Push through the working foot to return to standing."],
    ["Keep the working knee tracking with the toes.", "Sit the hips back rather than driving the knee far forward.", "Maintain control through the change of direction."],
  ),
  "machine row": guide(
    "Keep the torso supported and pull the machine handles toward the ribs by driving the elbows backward.",
    ["Adjust the seat and chest support so the handles can be reached without rounding forward.", "Plant the feet and keep the chest supported if the machine provides a pad.", "Begin with the arms long and shoulders controlled."],
    ["Pull the handles toward the ribs while driving the elbows backward.", "Pause briefly with the shoulder blades drawn together.", "Return under control until the arms are long again."],
    ["Do not rock the torso to move the weight.", "Keep the shoulders away from the ears.", "Avoid bouncing the weight stack."],
  ),
  "medicine ball rotation": guide(
    "Rotate through the hips and torso while controlling the medicine ball close enough to the body to stay balanced.",
    ["Stand in an athletic stance with the medicine ball held securely in front of the torso.", "Keep the knees soft and the trunk braced."],
    ["Rotate the hips and ribcage toward one side while guiding the ball with the torso.", "Return through center and rotate toward the other side as programmed.", "Pivot the feet naturally rather than twisting against fixed knees."],
    ["Create the rotation with the body, not only the arms.", "Keep the movement smooth instead of whipping the ball.", "Maintain balance throughout the change of direction."],
  ),
  "plank to downward dog": guide(
    "Move from a strong high plank into an inverted-V by pushing the floor away and sending the hips up and back.",
    ["Begin in a high plank with hands under or slightly wider than the shoulders.", "Brace the trunk and establish a straight body line."],
    ["Press through the hands and shift the hips upward and backward into a downward-dog position.", "Lengthen through the spine without forcing the heels to the floor.", "Shift forward under control to return to the plank."],
    ["Keep the shoulders active in both positions.", "Do not collapse through the lower back when returning to plank.", "Use a comfortable hamstring and shoulder range."],
  ),
  "prone y t w": guide(
    "Use small controlled shoulder-blade movements while lifting the arms into Y, T, and W positions from a prone position.",
    ["Lie face down with the neck neutral and the forehead supported if needed.", "Brace lightly through the trunk so the movement comes from the upper back and shoulders."],
    ["Lift the arms into a Y position using a small controlled range, then lower.", "Repeat in a T position with the arms out to the sides.", "Finish in a W position by bending the elbows and drawing the shoulder blades down and together."],
    ["Use very little or no external load.", "Keep the shoulders away from the ears.", "Do not crank the lower back to lift the arms higher."],
  ),
  "rear delt fly": guide(
    "Open the arms out to the sides with a soft elbow bend while the torso stays fixed and the shoulders remain down.",
    ["Hinge at the hips or use chest support so the torso is stable.", "Let the dumbbells hang beneath the shoulders with elbows softly bent."],
    ["Raise the arms out and slightly back until the rear shoulders and upper back are engaged.", "Pause briefly without shrugging.", "Lower the dumbbells slowly to the start."],
    ["Do not swing the torso.", "Lead with the elbows rather than the hands.", "Use a light enough load to keep the shoulder blades controlled."],
  ),
  "resistance band row": guide(
    "Row the band toward the ribs while the torso stays tall and the shoulder blades move under control.",
    ["Secure the band to a stable anchor around mid-torso height.", "Step back until there is light tension with the arms extended.", "Brace the trunk and keep the shoulders relaxed."],
    ["Drive the elbows backward and pull the hands toward the ribs.", "Pause briefly as the shoulder blades draw together.", "Return slowly until the arms are extended again."],
    ["Confirm the anchor is secure before starting.", "Do not lean backward to finish the row.", "Keep the shoulders away from the ears."],
  ),
  "reverse snow angels": guide(
    "Sweep the arms from overhead toward the hips while lying prone, using the upper back without forcing lumbar extension.",
    ["Lie face down with the arms reaching overhead and the neck neutral.", "Brace lightly through the abdomen and glutes."],
    ["Lift the arms only slightly from the floor and sweep them outward toward the hips.", "Keep the shoulder blades controlled as the hands travel beside the body.", "Reverse the same path back overhead."],
    ["Keep the movement small and controlled.", "Do not crank the chest high by overextending the lower back.", "Keep the shoulders away from the ears."],
  ),
  "ring dip": guide(
    "Keep the rings close and stable as you lower through a controlled shoulder range and press back to support.",
    ["Begin in a stable top support with arms straight and rings close to the body.", "Brace the trunk and keep the shoulders down away from the ears."],
    ["Bend the elbows and lower while keeping the rings controlled beside the torso.", "Stop at the deepest position you can maintain without losing shoulder control.", "Press the rings down and return to a stable straight-arm support."],
    ["Do not let the rings drift far away from the body.", "Avoid dropping quickly into the bottom.", "Use assistance or a reduced range until the rings remain stable."],
  ),
  "ring row": guide(
    "Keep the body rigid and pull the rings toward the ribs without letting the hips sag.",
    ["Hold the rings with straight arms and walk the feet forward to choose an appropriate body angle.", "Brace the abdomen and glutes so the body forms one straight line."],
    ["Pull the rings toward the ribs while driving the elbows backward.", "Pause briefly with the chest approaching the rings.", "Lower under control until the arms are straight."],
    ["Keep the hips aligned with the shoulders and heels.", "Move the feet farther back to make the exercise easier.", "Do not shrug the shoulders toward the ears."],
  ),
  "rope pushdown": guide(
    "Keep the upper arms fixed beside the torso and straighten the elbows, separating the rope slightly at the bottom.",
    ["Face the cable with a rope attachment and place the elbows close to the ribs.", "Brace the trunk and keep the shoulders down."],
    ["Extend the elbows to press the rope downward.", "Separate the rope ends slightly near full extension without moving the upper arms backward.", "Return under control until the forearms rise while the elbows stay near the torso."],
    ["Do not rock bodyweight onto the cable.", "Keep the upper arms still.", "Maintain neutral wrists."],
  ),
  "russian twist": guide(
    "Rotate the ribcage from side to side while maintaining a controlled seated trunk position.",
    ["Sit with knees bent and lean back only as far as you can keep the spine controlled.", "Keep the feet planted for more stability or lift them only if the position remains solid."],
    ["Rotate the chest and shoulders toward one side.", "Return through center and rotate toward the opposite side.", "Use a deliberate range rather than racing the hands from side to side."],
    ["Rotate the torso instead of moving only the arms.", "Keep the chest lifted rather than collapsing into the lower back.", "Reduce the lean or range if the back position becomes uncomfortable."],
  ),
  "scapular pull up": guide(
    "Keep the elbows straight and move the body by drawing the shoulder blades down from a controlled hang.",
    ["Take a secure pull-up-bar grip and begin in a controlled long-arm hang.", "Keep the elbows straight and the trunk quiet."],
    ["Pull the shoulder blades down and slightly together without bending the elbows.", "Allow the body to rise a small distance as the shoulders move away from the ears.", "Return slowly to the long-arm position."],
    ["The movement is intentionally small.", "Do not turn it into a regular pull-up.", "Avoid swinging or kipping."],
  ),
  "scapular push up": guide(
    "Keep the elbows straight while the shoulder blades glide together and apart in a strong plank position.",
    ["Begin in a high plank with hands under the shoulders and elbows straight.", "Brace the abdomen and glutes so the body stays rigid."],
    ["Allow the chest to sink slightly as the shoulder blades move toward each other without bending the elbows.", "Push the floor away to spread the shoulder blades apart.", "Repeat through a small controlled range."],
    ["Keep the elbows straight.", "Do not let the lower back sag.", "Think shoulder-blade motion rather than a push-up."],
  ),
  "shoulder taps": guide(
    "Maintain a stable high plank while alternating hand-to-opposite-shoulder taps with minimal hip rotation.",
    ["Begin in a high plank with the feet slightly wider than normal for stability.", "Brace the abdomen and glutes and keep the shoulders over the hands."],
    ["Shift weight into one hand and tap the opposite shoulder with the free hand.", "Return the hand to the floor and repeat on the other side.", "Move slowly enough to keep the pelvis nearly level."],
    ["Limit side-to-side hip rotation.", "Keep the body in one straight line.", "Widen the feet if more stability is needed."],
  ),
  "side plank": guide(
    "Stack the body in a straight line on one side and keep the supporting shoulder strong while the hips stay lifted.",
    ["Place the supporting elbow under the shoulder or use a straight-arm position if programmed.", "Stack or stagger the feet and align the hips with the shoulders."],
    ["Lift the hips until the body forms a straight line from head to feet.", "Maintain the position for the programmed time while breathing normally.", "Lower under control before changing sides."],
    ["Do not let the hips sag toward the floor.", "Keep the supporting shoulder away from the ear.", "Use the knees as the support point for an easier variation."],
  ),
  "side shuffles": guide(
    "Stay in an athletic stance and move laterally with quick controlled steps without crossing the feet.",
    ["Stand with feet around shoulder width, knees softly bent, and hips slightly back.", "Keep the chest up and weight balanced through the midfoot."],
    ["Push from the trailing leg and step laterally with the lead foot.", "Follow with the trailing foot while maintaining stance width.", "Continue for the programmed distance or time, then change direction."],
    ["Do not cross the feet.", "Stay low enough to change direction under control.", "Keep the knees aligned with the toes."],
  ),
  "single arm dumbbell row": guide(
    "Brace the torso and pull the dumbbell toward the hip while keeping the spine and pelvis stable.",
    ["Support the non-working hand on a bench or stable surface and establish a strong hip hinge.", "Let the working arm hang beneath the shoulder with the spine neutral."],
    ["Drive the working elbow back toward the hip or lower ribs.", "Pause briefly without rotating the torso open.", "Lower the dumbbell until the arm is long again."],
    ["Keep the shoulders and hips mostly square to the floor.", "Do not jerk the torso to move the weight.", "Lead with the elbow rather than curling the dumbbell."],
  ),
  "skull crushers": guide(
    "Keep the upper arms steady and bend only at the elbows to lower the bar toward the forehead or just behind the head.",
    ["Lie securely on a bench holding the bar or EZ-bar above the chest with arms extended.", "Angle the upper arms slightly back if needed so the elbows remain comfortable."],
    ["Bend the elbows and lower the bar under control toward the forehead or just behind the head.", "Keep the upper arms mostly fixed.", "Extend the elbows to return to the starting position."],
    ["Do not let the elbows flare widely.", "Use a controlled load and lowering speed.", "Keep the wrists stacked and comfortable."],
  ),
  "standing band rotation": guide(
    "Rotate away from the band anchor using the torso and hips while maintaining balance and steady band tension.",
    ["Anchor the band securely around chest height and stand side-on to the anchor.", "Hold the band with both hands in front of the torso and take a stable stance."],
    ["Rotate the ribcage and hips away from the anchor while the arms stay connected to the torso.", "Allow the feet to pivot naturally as needed.", "Return slowly toward the anchor against the band's pull."],
    ["Do not twist only through the lower back.", "Keep tension controlled on the return.", "Confirm the band anchor is secure before starting."],
  ),
  "superman pull": guide(
    "From a prone position, lightly lift the upper body and pull the elbows toward the ribs before reaching the arms forward again.",
    ["Lie face down with the legs long and the arms reaching overhead.", "Keep the neck neutral and lightly brace the abdomen and glutes."],
    ["Lift the hands and upper chest only slightly from the floor without forcing the lower back.", "Pull the elbows down toward the ribs while drawing the shoulder blades down and together.", "Reach the arms forward again under control, then lower or repeat as programmed."],
    ["Keep the lift small and controlled.", "Do not crank the neck upward.", "Focus on the upper-back pull rather than excessive lower-back extension."],
  ),
  "wall sit": guide(
    "Hold a controlled squat against the wall with the feet planted and the knees tracking with the toes.",
    ["Place the back against a wall and step the feet forward far enough to create a stable base.", "Slide down to a knee angle you can hold comfortably and control."],
    ["Press the back gently into the wall and hold the position for the programmed time.", "Keep even pressure through both feet.", "Stand up under control when the interval ends."],
    ["Keep the knees aligned with the toes.", "Do not force a specific depth if it causes knee discomfort.", "Keep the heels planted."],
  ),
  "wall slides": guide(
    "Slide the arms upward against the wall while keeping the ribs controlled and the shoulders moving without shrugging.",
    ["Stand with the upper back against a wall and feet slightly forward.", "Bring the arms into a comfortable goalpost position with the elbows bent.", "Keep the ribs stacked rather than arching the lower back to reach the wall."],
    ["Slide the arms upward as far as you can while maintaining controlled shoulder position.", "Pause briefly at the highest comfortable point.", "Slide the arms back down without shrugging."],
    ["Do not force the hands or elbows flat if mobility does not allow it.", "Keep the ribs from flaring upward.", "Move slowly and stay within a pain-free shoulder range."],
  ),

});

export const getExerciseAlternativeFormGuide = (exerciseName = '') =>
  GUIDES[normalizeExerciseName(exerciseName)] || null;
